import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Send, MapPin, Trash2, Upload, Image as ImageIcon } from 'lucide-react';
import { ServiceAdvisorService } from '../../services/serviceAdvisorService';
import { cleanPhoneNumber } from '../../utils/phoneUtils';

const ComplaintRegistration = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  
  const [complaintData, setComplaintData] = useState({
    customerName: '',
    phoneNumber: '',
    vehicleDetails: '',
    source: '',
    locationLink: '',
    quotedAmount: '',
    advancePayment: '',
    problemDescription: '',
    alternateNumber: '',
    serviceType: '',
    dropLocation: '',
  });

  const [customSource, setCustomSource] = useState('');
  const [isOtherSourceSelected, setIsOtherSourceSelected] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDataRestored, setIsDataRestored] = useState(false);

  // Form data persistence key
  const FORM_DATA_KEY = `complaint_draft_${user?.id || 'guest'}`;

  // Save form data to localStorage
  const saveFormData = (data) => {
    try {
      localStorage.setItem(FORM_DATA_KEY, JSON.stringify({
        ...data,
        timestamp: new Date().toISOString()
      }));
      console.log('📝 Form data saved successfully');
    } catch (error) {
      console.error('❌ Error saving form data:', error);
    }
  };

  // Load saved form data from localStorage
  const loadSavedFormData = () => {
    try {
      const savedData = localStorage.getItem(FORM_DATA_KEY);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        // Check if data is not too old (24 hours)
        const savedTime = new Date(parsedData.timestamp);
        const now = new Date();
        const hoursDiff = (now.getTime() - savedTime.getTime()) / (1000 * 60 * 60);
        
        if (hoursDiff < 24) {
          // Remove timestamp before setting data
          const { timestamp, ...formData } = parsedData;
          setComplaintData(formData);
          setIsOtherSourceSelected(formData.source === 'other');
          setCustomSource(formData.source === 'other' ? formData.source : '');
          console.log('📋 Saved form data restored successfully');
          return true;
        } else {
          // Data is too old, remove it
          localStorage.removeItem(FORM_DATA_KEY);
          console.log('🗑️ Old form data removed');
        }
      }
    } catch (error) {
      console.error('❌ Error loading saved form data:', error);
    }
    return false;
  };

  // Clear saved form data
  const clearSavedFormData = () => {
    try {
      localStorage.removeItem(FORM_DATA_KEY);
      console.log('🗑️ Form data cleared successfully');
    } catch (error) {
      console.error('❌ Error clearing form data:', error);
    }
  };

  // Load saved form data on component mount
  useEffect(() => {
    const restored = loadSavedFormData();
    setIsDataRestored(restored);
  }, []);

  // Initialize other source selection state
  useEffect(() => {
    setIsOtherSourceSelected(complaintData.source === 'other');
  }, [complaintData.source]);

  const sourceOptions = [
    { label: 'Select Source', value: '' },
    { label: 'Google Ads', value: 'google_ads' },
    { label: 'Roadserve Membership', value: 'roadserve_membership' },
    { label: 'Toll Free', value: 'toll_free' },
    { label: 'Other', value: 'other' },
  ];

  const serviceTypeOptions = [
    { label: 'Select Service Type', value: '' },
    { label: 'Emergency Roadside Assistance', value: 'emergency_roadside' },
    { label: 'Towing Service', value: 'towing' },
    { label: 'Jump Start', value: 'jump_start' },
    { label: 'Flat Tyre Change', value: 'flat_tyre' },
    { label: 'Fuel Delivery', value: 'fuel_delivery' },
    { label: 'Lockout Service', value: 'lockout' },
    { label: 'Other', value: 'other' },
  ];

  const handleInputChange = (field, value) => {
    // Clean phone numbers when they are entered
    if (field === 'phoneNumber' || field === 'alternateNumber') {
      value = cleanPhoneNumber(value);
    }
    
    const updatedData = {
      ...complaintData,
      [field]: value,
    };
    
    setComplaintData(updatedData);
    
    // Auto-save form data (debounced)
    setTimeout(() => {
      saveFormData(updatedData);
    }, 500);
  };

  const handleSourceChange = (value) => {
    const updatedData = { ...complaintData, source: value };
    setComplaintData(updatedData);
    setIsOtherSourceSelected(value === 'other');
    if (value !== 'other') {
      setCustomSource('');
    }
    // Auto-save
    setTimeout(() => {
      saveFormData(updatedData);
    }, 500);
  };

  const handleServiceTypeChange = (value) => {
    const updatedData = { ...complaintData, serviceType: value };
    setComplaintData(updatedData);
    // Auto-save
    setTimeout(() => {
      saveFormData(updatedData);
    }, 500);
  };

  const handleCustomSourceChange = (value) => {
    setCustomSource(value);
    setComplaintData(prev => ({ ...prev, source: value }));
  };

  const handleMediaSelected = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 5) {
      alert('Maximum 5 files allowed');
      return;
    }
    setSelectedMedia(files);
  };

  const handleLocationRequest = async () => {
    if (complaintData.phoneNumber.length !== 10) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      const result = await ServiceAdvisorService.sendWhatsAppLocationRequest(complaintData.phoneNumber);
      
      if (result.success) {
        alert('Success: ' + result.message);
      } else {
        alert('Error: ' + result.message);
      }
    } catch (error) {
      console.error('Location request error:', error);
      alert('Failed to send location request. Please check your internet connection and try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - exactly like mobile app
    if (!complaintData.customerName.trim()) {
      alert('Error: Customer name is required');
      return;
    }
    if (!complaintData.phoneNumber.trim()) {
      alert('Error: Phone number is required');
      return;
    }
    if (!complaintData.vehicleDetails.trim()) {
      alert('Error: Vehicle details are required');
      return;
    }
    if (!complaintData.problemDescription.trim()) {
      alert('Error: Problem description is required');
      return;
    }
    if (!complaintData.serviceType.trim()) {
      alert('Error: Service type is required');
      return;
    }
    if (complaintData.serviceType === 'towing' && !complaintData.dropLocation.trim()) {
      alert('Error: Drop location is required for towing service');
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload media files to Supabase Storage if any
      let uploadedMediaUrls = [];
      if (selectedMedia.length > 0) {
        console.log('📸 Uploading media files during registration:', selectedMedia.length, 'files');
        
        // TODO: Implement media upload to Supabase Storage
        // For now, we'll skip media upload but keep the structure
        console.log('⚠️ Media upload not yet implemented for web app');
      }

      // Prepare data for the service - exactly like mobile app
      const serviceRequestData = {
        customer_name: complaintData.customerName,
        vehicle_number: complaintData.vehicleDetails,
        service_type: complaintData.serviceType,
        priority: 'medium', // Default priority
        contact_number: complaintData.phoneNumber,
        alternate_number: complaintData.alternateNumber || '',
        problem: complaintData.problemDescription,
        address: '', // Required by DB but not shown in form
        pincode: '', // Required by DB but not shown in form
        customer_quoted_amount: parseFloat(complaintData.quotedAmount) || 0,
        advance_payment: complaintData.advancePayment || '',
        service_tag: '', // Empty since field was removed from form but required by DB
        location_link: complaintData.locationLink || '',
        media_upload: uploadedMediaUrls,
        source: complaintData.source || 'web',
        remark: '',
        is_premium: false,
        card_number: '',
        expected_completion_date: undefined,
        drop_location: complaintData.dropLocation || undefined,
      };

      // Create service request using the service
      console.log('Calling createServiceRequest with:', { serviceRequestData, executiveId: user?.id, executiveName: user?.name });
      console.log('ServiceAdvisorService methods:', Object.getOwnPropertyNames(ServiceAdvisorService));
      console.log('createServiceRequest exists?', typeof ServiceAdvisorService.createServiceRequest);
      
      if (typeof ServiceAdvisorService.createServiceRequest !== 'function') {
        throw new Error('createServiceRequest method not found in ServiceAdvisorService');
      }
      
      const result = await ServiceAdvisorService.createServiceRequest(
        serviceRequestData,
        user?.id || '',
        user?.name || ''
      );

      if (result.success) {
        // Clear saved form data after successful submission
        clearSavedFormData();
        
        // After successful complaint creation, send WhatsApp location request
        console.log('📱 Sending WhatsApp location request after complaint creation...');
        try {
          const whatsappResult = await ServiceAdvisorService.sendWhatsAppLocationRequest(complaintData.phoneNumber);
          
          if (whatsappResult.success) {
            // Update WhatsApp location sent status in database
            if (result.requestId) {
              await ServiceAdvisorService.updateWhatsAppLocationSent(result.requestId);
              console.log('✅ WhatsApp location sent and database updated successfully');
            }
          } else {
            console.log('⚠️ WhatsApp location request failed:', whatsappResult.message);
          }
        } catch (whatsappError) {
          console.error('❌ Error sending WhatsApp location request:', whatsappError);
          // Don't show error to user as complaint was created successfully
        }

        alert('Success: ' + result.message);
        navigate('/service_advisor/complaints');
      } else {
        alert('Error: ' + result.message);
      }
    } catch (error) {
      console.error('Error submitting complaint:', error);
      alert('Error: Failed to submit complaint. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/service_advisor/complaints')}
              className="p-2 hover:bg-white/20 rounded-lg"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Complaint Registration</h1>
            </div>
          </div>
          {isDataRestored && (
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to clear the saved draft? This action cannot be undone.')) {
                  clearSavedFormData();
                  setComplaintData({
                    customerName: '',
                    phoneNumber: '',
                    vehicleDetails: '',
                    source: '',
                    locationLink: '',
                    quotedAmount: '',
                    advancePayment: '',
                    problemDescription: '',
                    alternateNumber: '',
                    serviceType: '',
                    dropLocation: '',
                  });
                  setIsDataRestored(false);
                  setIsOtherSourceSelected(false);
                  setCustomSource('');
                  alert('Draft Cleared: Form has been reset to empty state.');
                }
              }}
              className="p-2 hover:bg-white/20 rounded-lg"
              title="Clear Draft"
            >
              <Trash2 size={20} />
            </button>
          )}
          {!isDataRestored && <div className="w-10" />}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Customer Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={complaintData.customerName}
              onChange={(e) => handleInputChange('customerName', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              placeholder="Enter customer name"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="tel"
                value={complaintData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                placeholder="Enter phone number"
                maxLength={15}
              />
              {complaintData.phoneNumber.length === 10 && (
                <button
                  type="button"
                  onClick={handleLocationRequest}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center space-x-2"
                >
                  <MapPin size={18} />
                  <span>Location</span>
                </button>
              )}
            </div>
            {complaintData.phoneNumber.length > 0 && complaintData.phoneNumber.length < 10 && (
              <p className="text-sm text-gray-500 mt-1 italic">
                Enter 10-digit mobile number. Country codes will be automatically removed.
              </p>
            )}
            {complaintData.phoneNumber.length === 10 && (
              <p className="text-sm text-green-600 mt-1 font-medium">
                ✓ Valid phone number
              </p>
            )}
          </div>

          {/* Vehicle Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vehicle Details <span className="text-red-500">*</span>
            </label>
            <textarea
              value={complaintData.vehicleDetails}
              onChange={(e) => handleInputChange('vehicleDetails', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              placeholder="Enter vehicle number, model, color, etc."
              rows={3}
            />
          </div>

          {/* Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Source</label>
            <select
              value={complaintData.source}
              onChange={(e) => handleSourceChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            >
              {sourceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {isOtherSourceSelected && (
              <input
                type="text"
                value={customSource}
                onChange={(e) => handleCustomSourceChange(e.target.value)}
                className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                placeholder="Specify other source"
              />
            )}
          </div>

          {/* Location Link */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location Link</label>
            <input
              type="text"
              value={complaintData.locationLink}
              onChange={(e) => handleInputChange('locationLink', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              placeholder="Enter location link or coordinates"
            />
          </div>

          {/* Quoted Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Customer Quoted Amount</label>
            <input
              type="number"
              value={complaintData.quotedAmount}
              onChange={(e) => handleInputChange('quotedAmount', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              placeholder="Enter quoted amount"
            />
          </div>

          {/* Advance Payment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Advance Payment</label>
            <input
              type="text"
              value={complaintData.advancePayment}
              onChange={(e) => handleInputChange('advancePayment', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              placeholder="Enter advance payment amount"
            />
          </div>

          {/* Upload Media */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload Media</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleMediaSelected}
                className="hidden"
                id="media-upload"
              />
              <label
                htmlFor="media-upload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <Upload size={32} className="text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  Click to upload images (Max 5 files)
                </span>
              </label>
              {selectedMedia.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
                  {selectedMedia.map((file, index) => (
                    <div key={index} className="relative">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newMedia = selectedMedia.filter((_, i) => i !== index);
                          setSelectedMedia(newMedia);
                        }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={complaintData.problemDescription}
              onChange={(e) => handleInputChange('problemDescription', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              placeholder="Describe the problem in detail"
              rows={4}
            />
          </div>

          {/* Alternate Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Alternate Number</label>
            <input
              type="tel"
              value={complaintData.alternateNumber}
              onChange={(e) => handleInputChange('alternateNumber', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              placeholder="Enter alternate number"
              maxLength={15}
            />
          </div>

          {/* Service Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Type <span className="text-red-500">*</span>
            </label>
            <select
              value={complaintData.serviceType}
              onChange={(e) => handleServiceTypeChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            >
              {serviceTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Drop Location - Only show for towing service */}
          {complaintData.serviceType === 'towing' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Drop Location <span className="text-red-500">*</span>
              </label>
              <textarea
                value={complaintData.dropLocation}
                onChange={(e) => handleInputChange('dropLocation', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                placeholder="Enter drop location for towing service"
                rows={3}
              />
            </div>
          )}

          {/* Submit Button */}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/service_advisor/complaints')}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center space-x-2"
            >
              <X size={18} />
              <span>Cancel</span>
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Send size={18} />
                  <span>Send to Associated Advisor</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ComplaintRegistration;
